import React, { useState, useEffect } from 'react';
import {
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  CircularProgress,
  Divider,
  Chip } from
'@mui/material';
import { ticketsApi } from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import ResponsiveDialog from '../ResponsiveDialog';

/**
 * Dialog to select and apply existing solutions to a ticket
 * 
 * @param {number} ticketId - The ID of the ticket to apply solutions to
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Function to call when the dialog is closed
 * @param {function} onApplied - Function to call when solutions are applied
 */import { useThemeSettings } from "../../contexts/ThemeContext";
export function SelectExistingSolution({ ticketId, open, onClose, onApplied }) {const { t } = useThemeSettings();
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSolutions, setSelectedSolutions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [applying, setApplying] = useState(false);

  // Get user data and check permissions
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const userRole = user?.role || user?.userRole || 'user';
  const isSupport = userRole === 'support' || userRole === 'admin' || hasPermission('tickets.manage');

  // Only support users and admins can apply existing solutions
  if (!isSupport) {
    return null;
  }

  // Fetch solutions when the dialog opens
  useEffect(() => {
    if (open) {
      fetchSolutions();
    }
  }, [open]);

  const fetchSolutions = async () => {
    setLoading(true);
    try {
      // Fetch solutions from API
      const response = await ticketsApi.getSolutions();

      let solutionsData = [];
      if (response?.data) {
        if (Array.isArray(response.data)) {
          solutionsData = response.data;
        } else if (response.data.solutions) {
          solutionsData = response.data.solutions;
        }
      }

      // Transform data if needed
      const transformedSolutions = solutionsData.map((solution) => ({
        id: solution.id || solution._id,
        title: solution.title || solution.name,
        description: solution.description,
        complexity: solution.complexity || 'moderate',
        successCount: solution.successCount || solution.success_count || 0,
        totalUses: solution.totalUses || solution.total_uses || 0,
        stepByStepInstructions: solution.stepByStepInstructions || solution.step_by_step_instructions
      }));

      setSolutions(transformedSolutions);
    } catch (error) {
      console.error('Error fetching solutions:', error);
      alert('Failed to load solutions');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSolution = (solutionId) => {
    setSelectedSolutions((prev) => {
      if (prev.includes(solutionId)) {
        return prev.filter((id) => id !== solutionId);
      } else {
        return [...prev, solutionId];
      }
    });
  };

  const handleApplySolutions = async () => {
    if (selectedSolutions.length === 0) {
      alert('Please select at least one solution');
      return;
    }

    setApplying(true);
    try {
      // Call the API to apply the solutions to the ticket
      try {
        // Use the /tickets/:id/solutions endpoint to apply solutions
        await ticketsApi.applySolution(ticketId, {
          solutionIds: selectedSolutions
        });

        //console.log(`Applied solutions ${selectedSolutions.join(', ')} to ticket ${ticketId}`);
        alert('Solutions applied successfully');
      } catch (error) {
        console.error('Error applying solutions:', error);
        throw error; // Re-throw to be caught by the outer catch block
      }
      onApplied && onApplied();
      onClose();
    } catch (error) {
      console.error('Error applying solutions:', error);
      alert('Failed to apply solutions');
    } finally {
      setApplying(false);
    }
  };

  // Filter solutions based on search term
  const filteredSolutions = solutions.filter((solution) =>
  solution.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
  solution.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Helper function to format success rate
  const formatSuccessRate = (successCount, totalUses) => {
    if (!totalUses) return 'New';
    const rate = Math.round(successCount / totalUses * 100);
    return `${rate}% (${successCount}/${totalUses})`;
  };

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t ? t("SelectExistingSolution.select_solutions_apply") : "Select Solutions to Apply"}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label={t ? t("SelectExistingSolution.search_solutions") : "Search Solutions"}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          margin="normal"
          placeholder={t ? t("SelectExistingSolution.search_title_description") : "Search by title or description..."} />
        

        {loading ?
        <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box> :

        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {filteredSolutions.length === 0 ?
          <Typography color="text.secondary" align="center" p={2}>{t ? t("SelectExistingSolution.no_solutions_found") : "No solutions found"}

          </Typography> :

          filteredSolutions.map((solution) =>
          <React.Fragment key={solution.id}>
                  <ListItem
              alignItems="flex-start"
              button
              onClick={() => handleToggleSolution(solution.id)}>
              
                    <ListItemIcon>
                      <Checkbox
                  edge="start"
                  checked={selectedSolutions.includes(solution.id)}
                  tabIndex={-1}
                  disableRipple />
                
                    </ListItemIcon>
                    <ListItemText
                primary={
                <Box display="flex" alignItems="center">
                          {solution.title}
                          <Chip
                    label={solution.complexity}
                    size="small"
                    color={
                    solution.complexity === 'simple' ? 'success' :
                    solution.complexity === 'moderate' ? 'warning' : 'error'
                    }
                    sx={{ ml: 1 }} />
                  
                        </Box>
                }
                secondary={
                <>
                          <Typography
                    component="span"
                    variant="body2"
                    color="text.primary">{t ? t("SelectExistingSolution.success_rate") : "Success rate:"}

                    {formatSuccessRate(solution.successCount, solution.totalUses)}
                          </Typography>
                          <Typography
                    component="p"
                    variant="body2">
                    
                            {solution.description}
                          </Typography>
                        </>
                } />
              
                  </ListItem>
                  <Divider variant="inset" component="li" />
                </React.Fragment>
          )
          }
          </List>
        }
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t ? t("LocationDialog.cancel") : "Cancel"}</Button>
        <Button
          onClick={handleApplySolutions}
          variant="contained"
          color="primary"
          disabled={selectedSolutions.length === 0 || applying}>
          
          {applying ? 'Applying...' : `Apply ${selectedSolutions.length} Solutions`}
        </Button>
      </DialogActions>
    </ResponsiveDialog>);

}